import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from './App';
import { useTenantStore } from './state/tenant';
import './styles/tokens.css';
import './styles/index.css';
import './styles/ui-refresh.css';

const queryClient = new QueryClient();

// Casi ninguna queryKey incluye la organización, así que al cambiar de empresa (o cerrar sesión)
// la caché de la organización anterior se descarta. El listener de zustand corre síncrono dentro
// de set(), antes del siguiente render, para que ninguna vista pinte datos de la otra empresa.
useTenantStore.subscribe((state, prev) => {
  if (prev.activeOrganizationId === null || state.activeOrganizationId === prev.activeOrganizationId) return;
  void queryClient.cancelQueries();
  void queryClient.resetQueries();
});

const CHUNK_RELOAD_KEY = 'tesoreria:chunk-reload-once';

const reloadOnce = () => {
  const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
  if (alreadyReloaded) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  window.location.reload();
};

window.addEventListener(
  'error',
  (event) => {
    const target = event.target as HTMLScriptElement | null;
    const isChunkScriptError =
      target?.tagName === 'SCRIPT' && typeof target.src === 'string' && target.src.includes('/assets/');
    if (isChunkScriptError) {
      reloadOnce();
    }
  },
  true,
);

window.addEventListener('unhandledrejection', (event) => {
  const reasonText = String((event as PromiseRejectionEvent).reason ?? '');
  if (reasonText.includes('Failed to fetch dynamically imported module')) {
    reloadOnce();
  }
});

const rootElement = document.getElementById('root');
if (rootElement) {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
        {import.meta.env.VITE_SHOW_QUERY_DEVTOOLS === 'true' ? (
          <ReactQueryDevtools initialIsOpen={false} />
        ) : null}
      </QueryClientProvider>
    </StrictMode>,
  );
}
